import { Injectable, inject } from "@angular/core";
import {
  ActivatedRouteSnapshot,
  Route,
  Router,
  RouterStateSnapshot,
  UrlSegment,
  UrlTree
} from "@angular/router";
import { Observable } from "rxjs";
import { AppFeature } from "../../environments/features";
import { LoggingService, LogLevel } from "../_services/logging.service";
import { FeatureAccessService } from "../_services/feature-access.service";
import { ReviewSessionService } from "../_services/review-session.service";
import { UserService } from "../_services/user.service";

@Injectable({
  providedIn: "root"
})
export class HasRoleGuard {
  private authService = inject(UserService);
  private reviewSession = inject(ReviewSessionService);
  private router = inject(Router);
  private loggingService = inject(LoggingService);
  private featureAccess = inject(FeatureAccessService);

  canActivate(
    next: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ):
    | Observable<boolean | UrlTree>
    | Promise<boolean | UrlTree>
    | boolean
    | UrlTree {
    return this.checkUserLogin(next, state);
  }

  canActivateChild(
    next: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ):
    | Observable<boolean | UrlTree>
    | Promise<boolean | UrlTree>
    | boolean
    | UrlTree {
    return this.canActivate(next, state);
  }

  canDeactivate(
    component: unknown,
    currentRoute: ActivatedRouteSnapshot,
    currentState: RouterStateSnapshot,
    nextState?: RouterStateSnapshot
  ):
    | Observable<boolean | UrlTree>
    | Promise<boolean | UrlTree>
    | boolean
    | UrlTree {
    return true;
  }

  canLoad(
    route: Route,
    segments: UrlSegment[]
  ): Observable<boolean> | Promise<boolean> | boolean {
    return true;
  }

  private navigateToOnFailure(route: string, state?: RouterStateSnapshot) {
    if (state) {
      this.router.navigate([route], { queryParams: { returnUrl: state.url } });
      return;
    }
    this.router.navigate([route]);
  }

  private checkUserLogin(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): boolean {
    // Feature gating resolves here rather than at module import, because an
    // early-access flag depends on who is signed in (#438). A route with no
    // `feature` resolves to "disabled" and is refused: fail closed, and let
    // early-access.routes.spec.ts point at the offending route.
    const feature = route.data.feature as AppFeature;
    const access = this.featureAccess.access(feature);
    if (access === "disabled") {
      this.loggingService.log(
        "HasRoleGuard",
        "Feature not enabled for route: '" + state.url + "'! Navigating back to home!",
        LogLevel.DEBUG
      );
      this.navigateToOnFailure("home");
      return false;
    }
    if (access === "needsEarlyAccess") {
      // An anonymous visitor may well BE an eauser who is not signed in yet, so
      // the destination is preserved through the login round-trip. A signed-in
      // visitor without the role is bounced silently: signing in again would not
      // help, and naming the feature would advertise it outside the cohort.
      const authenticated = this.authService.checkAuthenticated();
      this.loggingService.log(
        "HasRoleGuard",
        "Early access required for route: '" + state.url + "'! Navigating to " +
          (authenticated ? "home" : "login") + "!",
        LogLevel.DEBUG
      );
      if (authenticated) {
        this.navigateToOnFailure("home");
      } else {
        this.navigateToOnFailure("login", state);
      }
      return false;
    }
    // A consented reviewer session (private-review flow, #226) is admitted WITHOUT a
    // Keycloak login, but ONLY to routes explicitly marked reviewerAccessible (study/:id,
    // dataset/:id) — never to admin/submission/etc. routes. Consent is required here so an
    // un-consented reviewer is kept on the /review consent screen rather than navigated into
    // a backend 403. The backend independently scopes+blinds these reads, so this is the
    // client-side half of a defence-in-depth gate, not the sole control.
    if (
      route.data.reviewerAccessible &&
      this.reviewSession.isReviewerMode() &&
      this.reviewSession.isConsented()
    ) {
      this.loggingService.log(
        "HasRoleGuard",
        "Admitting consented reviewer session for route: '" + state.url + "'",
        LogLevel.DEBUG
      );
      return true;
    }
    const requiresLogin = route.data.requiresLogin || false;
    if (requiresLogin) {
      if (this.authService.checkAuthenticated()) {
        if (route.data.role) {
          if (this.authService.hasRole(route.data.role)) {
            return true;
          }
          this.navigateToOnFailure("login", state);
          return false;
        } else {
          this.loggingService.log(
            "HasRoleGuard",
            "No role was defined for authenticated route " +
              route.pathFromRoot +
              ". This is most likely an error!",
            LogLevel.WARN
          );
          this.navigateToOnFailure("login", state);
          return false;
        }
      }
      this.navigateToOnFailure("login", state);
      return false;
    }
    return true;
  }
}
